import { Controller } from "@hotwired/stimulus"

/**
 * Toast Controller - Reusable toast notification system
 *
 * This controller provides a centralized toast notification system that can be
 * triggered from any part of the application via custom events.
 *
 * Usage examples:
 *
 * 1. Add controller to the page (usually in layout):
 *    <div data-controller="toast"
 *         data-toast-position-value="top-right"
 *         data-toast-auto-dismiss-value="5000">
 *    </div>
 *
 * 2. Trigger via JavaScript custom event:
 *    window.dispatchEvent(new CustomEvent('toast:show', {
 *      detail: { message: 'Hello World!', type: 'success' }
 *    }))
 *
 * 3. Use from another Stimulus controller:
 *    this.dispatch('toast:show', { detail: { message: 'Saved!', type: 'success' } })
 *
 * Available types: success, error, danger, warning, info, primary
 * Available positions: top-right, top-left, bottom-right, bottom-left, top-center, bottom-center
 */
export default class extends Controller {
  static values = {
    position: { type: String, default: "top-right" },
    autoDismiss: { type: Number, default: 5000 }
  }

  connect() {
    this.setupContainer()
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
    this.cleanupContainer()
  }

  setupContainer() {
    // Reuse existing container if present
    this.container = document.getElementById('toast-container')
    if (!this.container) {
      this.container = document.createElement('div')
      this.container.id = 'toast-container'
      this.container.className = this.getContainerClasses()
      this.container.setAttribute('aria-live', 'polite')
      this.container.setAttribute('aria-atomic', 'true')
      this.container.style.zIndex = '9999'
      document.body.appendChild(this.container)
    }
  }

  cleanupContainer() {
    // Only remove container if no toasts are present
    if (this.container && this.container.children.length === 0) {
      this.container.remove()
    }
  }

  setupEventListeners() {
    this.boundShowHandler = this.handleShowEvent.bind(this)
    window.addEventListener('toast:show', this.boundShowHandler)
  }

  removeEventListeners() {
    window.removeEventListener('toast:show', this.boundShowHandler)
  }

  handleShowEvent(event) {
    const { message, type, duration } = event.detail
    this.show(message, type, duration)
  }

  /**
   * Show a toast notification
   * @param {string} message - The message to display
   * @param {string} type - Toast type: success, error, danger, warning, info, primary
   * @param {number} duration - Optional override for auto-dismiss duration (ms)
   * @returns {HTMLElement} The toast element
   */
  show(message, type = 'info', duration = null) {
    const toast = this.createToast(message, type)
    this.container.appendChild(toast)

    // Animate in using requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      toast.classList.add('show')
    })

    // Auto dismiss
    const dismissTime = duration ?? this.autoDismissValue
    if (dismissTime > 0) {
      setTimeout(() => this.dismiss(toast), dismissTime)
    }

    return toast
  }

  createToast(message, type) {
    const toast = document.createElement('div')
    toast.className = `toast align-items-center border-0 mb-2 ${this.getTypeClasses(type)}`
    toast.setAttribute('role', 'alert')
    toast.setAttribute('aria-live', 'assertive')
    toast.setAttribute('aria-atomic', 'true')

    toast.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">
          <i class="${this.getIcon(type)} me-2"></i>
          ${this.escapeHtml(message)}
        </div>
        <button type="button" class="btn-close ${this.getCloseButtonClass(type)} me-2 m-auto"
                data-action="click->toast#dismissClick" aria-label="Close"></button>
      </div>
    `
    return toast
  }

  dismissClick(event) {
    const toast = event.target.closest('.toast')
    this.dismiss(toast)
  }

  dismiss(toast) {
    if (!toast || !toast.parentNode) return

    // Add fade-out animation
    toast.classList.remove('show')
    toast.classList.add('hiding')

    // Remove after animation completes
    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove()
      }
    }, 300)
  }

  getContainerClasses() {
    const positions = {
      'top-right': 'position-fixed top-0 end-0 p-3',
      'top-left': 'position-fixed top-0 start-0 p-3',
      'bottom-right': 'position-fixed bottom-0 end-0 p-3',
      'bottom-left': 'position-fixed bottom-0 start-0 p-3',
      'top-center': 'position-fixed top-0 start-50 translate-middle-x p-3',
      'bottom-center': 'position-fixed bottom-0 start-50 translate-middle-x p-3'
    }
    return `toast-container ${positions[this.positionValue] || positions['top-right']}`
  }

  getTypeClasses(type) {
    const types = {
      'success': 'text-bg-success',
      'error': 'text-bg-danger',
      'danger': 'text-bg-danger',
      'warning': 'text-bg-warning text-dark',
      'info': 'text-bg-info text-dark',
      'primary': 'text-bg-primary'
    }
    return types[type] || types['info']
  }

  getCloseButtonClass(type) {
    // Use white close button for dark backgrounds
    const darkBackgrounds = ['success', 'error', 'danger', 'primary']
    return darkBackgrounds.includes(type) ? 'btn-close-white' : ''
  }

  getIcon(type) {
    const icons = {
      'success': 'fas fa-check-circle',
      'error': 'fas fa-exclamation-triangle',
      'danger': 'fas fa-exclamation-triangle',
      'warning': 'fas fa-exclamation-circle',
      'info': 'fas fa-info-circle',
      'primary': 'fas fa-info-circle'
    }
    return icons[type] || icons['info']
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Public methods for programmatic use

  /**
   * Show success toast
   * @param {string} message
   * @param {number} duration
   */
  success(message, duration = null) {
    return this.show(message, 'success', duration)
  }

  /**
   * Show error toast
   * @param {string} message
   * @param {number} duration
   */
  error(message, duration = null) {
    return this.show(message, 'error', duration)
  }

  /**
   * Show warning toast
   * @param {string} message
   * @param {number} duration
   */
  warning(message, duration = null) {
    return this.show(message, 'warning', duration)
  }

  /**
   * Show info toast
   * @param {string} message
   * @param {number} duration
   */
  info(message, duration = null) {
    return this.show(message, 'info', duration)
  }

  /**
   * Clear all toasts
   */
  clearAll() {
    if (this.container) {
      while (this.container.firstChild) {
        this.dismiss(this.container.firstChild)
      }
    }
  }
}